import { OutboundShipmentKind } from "@redotech/cockroachdb-codegen/congodb/outbound-shipment-kind";
import { OutboundShipmentSource } from "@redotech/cockroachdb-codegen/congodb/outbound-shipment-source";
import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { Currency } from "@redotech/money/currencies";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { CarrierSchema } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { HazmatSchema } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import {
  ConsigneeType,
  ContentsType,
  NonDeliveryOption,
  OutboundShipmentTaxIdSchema,
  USPSExemptionCode,
} from "@redotech/redo-fulfillment-customs-common/customs";
import { LandedCostBillingSchema } from "@redotech/redo-fulfillment-customs-common/landed-cost-buy-options";
import {
  LengthSchema,
  MoneySchema,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { OutboundShipmentParcelV2Schema } from "@redotech/redo-fulfillment-parcel-common/outbound-shipment-parcel-v2";
import { CustomParcelType } from "@redotech/redo-fulfillment-parcel-common/parcel";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type { ProviderInfo, ShipmentPaymentOption } from "./outbound-labels";
import {
  CanadaPostOption,
  RateProvider,
  rawOutboundRateSchema,
  ShipmentPaymentOptionSchema,
  ShipmentRefundStatus,
  SpecialRateEligibility,
} from "./outbound-labels";

export enum TiktokShippingType {
  /** Tiktok provides shipping label */
  TIKTOK = "TIKTOK",
  /** Seller obtains shipping label and uploads tracking number to Tiktok */
  SELLER = "SELLER",
}

export enum TiktokShippingTypeTag {
  TIKTOK = "Shipping Type: Tiktok",
  SELLER = "Shipping Type: Seller",
}

export enum DhlEcommerceServiceEndorsement {
  AddressForwardingRequested = "1",
  ForwardingServiceRequested = "2",
  ChangeServiceRequested = "3",
  ReturnServiceRequested = "4",
}

export const DhlEcommerceServiceEndorsementSchema = createEnum(
  DhlEcommerceServiceEndorsement,
  { enumName: "DhlEcommerceServiceEndorsement" },
);

const dhlEcommerceLabelOptionsSchema = z.object({
  customLabelText1: z.string().nullish(),
  customLabelText2: z.string().nullish(),
  serviceEndorsement: DhlEcommerceServiceEndorsementSchema.nullish(),
});
export const DhlEcommerceLabelOptionsSchema: z.ZodType<DhlEcommerceLabelOptions> =
  dhlEcommerceLabelOptionsSchema;
export interface DhlEcommerceLabelOptions extends z.infer<
  typeof dhlEcommerceLabelOptionsSchema
> {}

// DHL partnership certification teams. These teams authenticate against DHL's
// certification environment (DHL_ECOMMERCE_CERTIFICATION_* credentials) and get
// access to the certification label options (custom label text, ancillary
// endorsement, weight-based direct return labels).
export const DHL_CERTIFICATION_TEAM_IDS = new Set<string>([
  "6a1db6a84ef1324b6e44d52b", // production DHL certification store
  "6a1dc49f3b6b1095b893011d", // local DHL test instance
  "69efe038e4f1a01e6b84d8a7", // Coleman's DHL direct-return team
]);

export const isDhlCertificationTeam = (
  teamId: string | null | undefined,
): boolean => !!teamId && DHL_CERTIFICATION_TEAM_IDS.has(teamId);

export const outboundShipmentAddressSchema = z.object({
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string(),
  country: z.string(),
  residential: z.boolean().nullish(),
  name: z.string().nullish(),
  company: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  consigneeType: createEnum(ConsigneeType, {
    enumName: "ConsigneeType",
  }).nullish(),
});

export interface OutboundShipmentAddress extends z.infer<
  typeof outboundShipmentAddressSchema
> {}

export const outboundShipmentAddressesEqual = (
  a: OutboundShipmentAddress,
  b: OutboundShipmentAddress,
) => {
  for (const key of Object.keys(a)) {
    if (
      a[key as keyof OutboundShipmentAddress] !==
      b[key as keyof OutboundShipmentAddress]
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Provider-neutral manufacturer info for carriers that require it on customs
 * items (e.g. Chit Chats U.S. DDP). `manufacturer` is the company / contact.
 */
const manufacturerCustomsFields = {
  manufacturer: z.string().nullish(),
  manufacturer_mid: z.string().nullish(),
  manufacturer_street: z.string().nullish(),
  manufacturer_street_2: z.string().nullish(),
  manufacturer_city: z.string().nullish(),
  manufacturer_state: z.string().nullish(),
  manufacturer_postal_code: z.string().nullish(),
  manufacturer_country: z.string().nullish(),
};

const OutboundShipmentCustomsItemSchema = z.object({
  object: z.string(),
  description: z.string(),
  quantity: z.number(),
  origin_country: z.string(),
  hs_tariff_number: z.string(),
  // This is total value
  value: z.string(),
  unit_value: z.string().nullish(),
  currency: createEnum(Currency, { enumName: "Currency" }).nullish(),
  weight: z.number(),
  code: z.string().nullish(),
  ...manufacturerCustomsFields,
  product_identifier: z.string().nullish(),
});

export interface OutboundShipmentCustomsItem extends z.infer<
  typeof OutboundShipmentCustomsItemSchema
> {}

const OutboundShipmentCustomsInfoSchema_ = z.object({
  customs_info: z
    .object({
      object: z.string(),
      contents_type: z.string(),
      contents_explanation: z.string().nullish(),
      additional_customs_comments: z.string().nullish(),
      customs_certify: z.boolean(),
      customs_signer: z.string(),
      eel_pfc: z.string(),
      non_delivery_option: z.string(),
      restriction_type: z.literal("none"),
      customs_items: z.array(OutboundShipmentCustomsItemSchema),
      declaration: z.string().nullish(),
    })
    .nullish(),
});
export const OutboundShipmentCustomsInfoSchema: z.ZodType<OutboundShipmentCustomsInfo> =
  OutboundShipmentCustomsInfoSchema_;

export interface OutboundShipmentCustomsInfo extends z.infer<
  typeof OutboundShipmentCustomsInfoSchema_
> {}

export enum OutboundShipmentDeliveryConfirmation {
  None = "NO_SIGNATURE",
  Signature = "SIGNATURE",
  Adult_Signature = "ADULT_SIGNATURE",
}

// who pays the taxes and duties on international shipments
// Setting this value to anything other than "DDP" will pass the cost and responsibility of duties on to the recipient of the package
export enum OutboundShipmentIncoterm {
  CFR = "CFR",
  CIF = "CIF",
  CIP = "CIP",
  CPT = "CPT",
  DAT = "DAT",
  DAP = "DAP",
  DDP = "DDP",
  EXW = "EXW",
  FAS = "FAS",
  FCA = "FCA",
  FOB = "FOB",
}

const OutboundShipmentParcelSchema = z.object({
  length: z.number().nullish(), // inches
  width: z.number().nullish(),
  height: z.number().nullish(),
  weight: z.number(), // Ounces
  predefined_package: z.string().nullish(),
  parcelType: createEnum(CustomParcelType, {
    enumName: "CustomParcelType",
  }).nullish(),
  additionalHandlingIndicator: z.boolean().nullish(),
  largePackageIndicator: z.boolean().nullish(),
  shipperReleaseIndicator: z.boolean().nullish(),
  customs_info: OutboundShipmentCustomsInfoSchema_.nullish(),
});

export interface OutboundShipmentParcel extends z.infer<
  typeof OutboundShipmentParcelSchema
> {}

export const OutboundShipmentParcelKeys = Object.keys(
  OutboundShipmentParcelSchema.shape,
) as (keyof OutboundShipmentParcel)[];

const OutboundShipmentCustomPrintFieldsSchema = z.object({
  invoice_numbers: z.array(z.string()), // The sales order number. Should probably be called that
  po_numbers: z.array(z.string()).nullish(), // Purchase order number
  skus: z.array(z.object({ sku: z.string(), quantity: z.number() })),
  customer_ids: z.array(z.string()),
  // Line-item product/item names to print in the label free-text field
  product_names: z.array(z.string()).nullish(),
  custom_metafield_values: z.array(z.string()).nullish(),
  // If an order spans multiple packages, which package is this
  package_info: z
    .object({ package_number: z.number(), total_packages: z.number() })
    .nullish(),
});

export interface OutboundShipmentCustomPrintFields extends z.infer<
  typeof OutboundShipmentCustomPrintFieldsSchema
> {}

const outboundShipmentIncotermSchema = createEnum(OutboundShipmentIncoterm, {
  enumName: "OutboundShipmentIncoterm",
});

// Total declared contents of the shipment, derived from order line items.
// Some carriers (e.g. Stallion Express) require a contents declaration on
// every shipment — even domestic — where no customs items are generated.
const outboundShipmentContentsSummarySchema = z.object({
  description: z.string(),
  value: z.number(),
  currency: createEnum(Currency, { enumName: "Currency" }),
});

export interface OutboundShipmentContentsSummary extends z.infer<
  typeof outboundShipmentContentsSummarySchema
> {}

// Provider-specific request inputs, shared by the V1 and V2 request schemas so
// they stay in lockstep. V2 augments only the `easypost` member (see
// createOutboundShipmentRequestV2Schema).
const outboundShipmentRequestProviderInfoSchema = z.object({
  easypost: z.object({ reference: z.string() }).nullish(),
  soapBox: z
    .object({
      batchId: z.string(),
      orderId: z.string(),
      customBoxId: z.string(),
      packageId: z.number(),
    })
    .nullish(),
  ups: z.object({ shipperId: z.string().nullish() }).nullish(),
  landmarkGlobal: z
    .object({
      totalShippingCost: z.string().nullish(),
      customerEmail: z.string().nullish(),
      customerPhone: z.string().nullish(),
    })
    .nullish(),
  fedex: z
    .object({
      accountNumber: z.string().nullish(),
      smartPostHubId: z.string().nullish(),
    })
    .nullish(),
  dhlEcommerce: z
    .object({
      pickupId: z.string(),
      distributionCenter: z.string(),
      packageId: z.string().nullish(),
      customLabelText1Override: z.string().nullish(),
      customLabelText2Override: z.string().nullish(),
      serviceEndorsement: DhlEcommerceServiceEndorsementSchema.nullish(),
    })
    .nullish(),
  globale: z
    .object({
      globalEOrderId: z.string().nullish(),
      lineItems: z
        .array(z.object({ sku: z.string(), quantity: z.number() }))
        .nullish(),
    })
    .nullish(),
  tiktok: z
    .object({
      shippingType: createEnum(TiktokShippingType, {
        enumName: "TiktokShippingType",
      }),
      tiktokOrderIds: z.array(z.string()),
      tiktokIntegrationId: z.string(),
    })
    .nullish(),
  amazonBuyShipping: z
    .object({
      amazonOrderId: z.string(),
      amazonIntegrationId: z.string(),
      amazonIntegrationTeamId: z.string(),
      shippingBusinessId: z.string(),
      items: z.array(
        z.object({
          amazonOrderItemId: z.string(),
          quantity: z.number().int().positive(),
          description: z.string(),
          weight: z.object({
            unit: createEnum(WeightUnit, { enumName: "WeightUnit" }),
            value: z.number(),
          }),
          value: MoneySchema,
        }),
      ),
    })
    .nullish(),
  uniUni: z
    .object({ pickupWarehouseId: z.number(), customerId: z.string() })
    .nullish(),
});

const outboundShipmentRequestBaseSchema = z.object({
  teamId: z.string(),
  from_address: outboundShipmentAddressSchema,
  to_address: outboundShipmentAddressSchema,
  return_address: outboundShipmentAddressSchema.nullish(),
  parcels: z.array(OutboundShipmentParcelSchema),
  is_return: z.boolean().nullish(),
  skip_one_rate: z.boolean().nullish(),
  // Redo USPS only
  showRedoLogoOnLabel: z.boolean().nullish(),
  delivery_confirmation: createEnum(OutboundShipmentDeliveryConfirmation, {
    enumName: "OutboundShipmentDeliveryConfirmation",
  }).nullish(),
  special_rates_eligibility: createEnum(SpecialRateEligibility, {
    enumName: "SpecialRateEligibility",
  }).nullish(),
  label_date: z.coerce.date().nullish(),
  tax_identifiers: z.array(OutboundShipmentTaxIdSchema).nullish(),
  incoterm: outboundShipmentIncotermSchema.nullish(),
  contents_summary: outboundShipmentContentsSummarySchema.nullish(),
  hazmat: HazmatSchema.nullish(),
  saturday_delivery: z.boolean().nullish(),
  customer_account_ids: z.array(z.string()).nullish(), // Unused, left only for backwards compatibility
  suppressCarrierCommercialInvoice: z.boolean().nullish(),
  dry_ice: z.boolean().nullish(),
  dry_ice_weight: z.string().nullish(),
  merchant_id: z.string().nullish(),
  carrier_accounts: z.array(z.string()).nullish(),
  payment: ShipmentPaymentOptionSchema.nullish(),
  blocked_providers: z
    .array(createEnum(RateProvider, { enumName: "RateProvider" }))
    .nullish(),
  carbon_neutral: z.boolean().nullish(),
  canada_post_option: createEnum(CanadaPostOption, {
    enumName: "CanadaPostOption",
  }).nullish(),
  direct_delivery_only: z.boolean().nullish(),
  custom_print_fields: OutboundShipmentCustomPrintFieldsSchema.nullish(),
  providerInfo: outboundShipmentRequestProviderInfoSchema.nullish(),
  duty_payment: LandedCostBillingSchema.nullish(),
  source: createEnum(OutboundShipmentSource, {
    enumName: "OutboundShipmentSource",
  }).nullish(),
});

const OutboundShipmentRequestSchema_ = outboundShipmentRequestBaseSchema.extend(
  {
    // Request overrides keyed by carrier then provider (RateProvider), applied
    // only to that carrier+provider rate sub-request. A carrier can be rated by
    // multiple providers, so the override is scoped to the specific pair.
    // Excluded from OutboundShipmentRequestKeys.
    overrides: z
      .record(
        z.string(),
        z.record(z.string(), outboundShipmentRequestBaseSchema.partial()),
      )
      .nullish(),
  },
);

export const OutboundShipmentRequestSchema: z.ZodType<CreateOutboundShipmentRequest> =
  OutboundShipmentRequestSchema_;

export interface CreateOutboundShipmentRequest extends z.infer<
  typeof OutboundShipmentRequestSchema_
> {}

type OutboundShipmentRequestBase = z.infer<
  typeof outboundShipmentRequestBaseSchema
>;

export const OutboundShipmentRequestKeys = Object.keys(
  outboundShipmentRequestBaseSchema.shape,
) as (keyof OutboundShipmentRequestBase)[];

// ---------------------------------------------------------------------------
// Outbound Shipment V2
//
// A fresh request shape for the rebuilt outbound shipping provider. It is
// intentionally NOT derived from the V1 base — it sheds legacy/per-parcel
// concerns and is authored field-by-field. Differences from V1:
//   1. Parcels carry their own units — length/width/height as { unit, value }
//      and weight as { unit, value } — instead of implicit inches/ounces.
//   2. Customs is declared once at the shipment level, not per parcel.
//   3. Tax identifiers are top-level (tax_identifiers) only — they describe the
//      whole shipment, never an individual parcel.
//   4. Hazmat, dry_ice, and dry_ice_weight are declared per parcel, not at the
//      shipment level. dry_ice_weight is unit-aware ({ unit, value }).
//   5. carrier_accounts moves under providerInfo.easypost.carrierAccounts.
//   6. customer_account_ids, skip_one_rate, and blocked_providers are dropped.
// ---------------------------------------------------------------------------

// V2 customs item. Like the V1 item but: no EasyPost `object` tag, weight is
// unit-aware ({ unit, value }) instead of implicit ounces, and unit_value is
// required (V2 always declares the per-unit value alongside the total `value`).
const outboundShipmentCustomsItemV2Schema = z.object({
  description: z.string(),
  quantity: z.number(),
  origin_country: z.string(),
  hs_tariff_number: z.string(),
  // Total value of the line (unit_value × quantity).
  total_value: z.string(),
  unit_value: z.string(),
  currency: createEnum(Currency, { enumName: "Currency" }).nullish(),
  weight: WeightSchema,
  code: z.string().nullish(),
  ...manufacturerCustomsFields,
  product_identifier: z.string().nullish(),
});

export interface OutboundShipmentCustomsItemV2 extends z.infer<
  typeof outboundShipmentCustomsItemV2Schema
> {}

// Shipment-level customs declaration. Mirrors the V1 per-parcel customs_info
// payload (minus EasyPost's `object` field), declared once for the whole
// shipment.
const outboundShipmentCustomsV2Schema = z.object({
  contents_type: z.string(),
  contents_explanation: z.string().nullish(),
  additional_customs_comments: z.string().nullish(),
  customs_certify: z.boolean(),
  customs_signer: z.string(),
  eel_pfc: z.string(),
  non_delivery_option: z.string(),
  restriction_type: z.literal("none"),
  customs_items: z.array(outboundShipmentCustomsItemV2Schema),
  declaration: z.string().nullish(),
});

export interface OutboundShipmentCustomsV2 extends z.infer<
  typeof outboundShipmentCustomsV2Schema
> {}

const outboundCustomsRequestItemSchema = z.object({
  description: z.string(),
  quantity: z.number(),
  unit_value: z.string(),
  currency: createEnum(Currency, { enumName: "Currency" }),
  unit_weight: WeightSchema,
  origin_country: z.string(),
  hs_tariff_number: z.string(),
  product_identifier: z.string().nullish(),
  ...manufacturerCustomsFields,
});

export interface OutboundCustomsRequestItem extends z.infer<
  typeof outboundCustomsRequestItemSchema
> {}

/**
 * A customs declaration before the team's settings are applied. Everything the
 * fulfillment-group path derives — certification, signer, EEL/PFC, declaration
 * text, per-line totals and weights — is absent here and filled in by
 * buildOutboundRateRequest, so an API caller cannot disagree with the merchant's
 * own customs configuration.
 */
const outboundCustomsRequestSchema = z.object({
  contents_type: createEnum(ContentsType, {
    enumName: "ContentsType",
  }).nullish(),
  contents_explanation: z.string().nullish(),
  non_delivery_option: createEnum(NonDeliveryOption, {
    enumName: "NonDeliveryOption",
  }).nullish(),
  usps_exemption_code: createEnum(USPSExemptionCode, {
    enumName: "USPSExemptionCode",
  }).nullish(),
  itn: z.string().nullish(),
  items: z.array(outboundCustomsRequestItemSchema),
});

export const OutboundCustomsRequestSchema: z.ZodType<OutboundCustomsRequest> =
  outboundCustomsRequestSchema;
export type OutboundCustomsRequest = z.infer<
  typeof outboundCustomsRequestSchema
>;

const createOutboundShipmentRequestV2BaseSchema = z.object({
  teamId: z.string(),
  from_address: outboundShipmentAddressSchema,
  to_address: outboundShipmentAddressSchema,
  return_address: outboundShipmentAddressSchema.nullish(),
  parcels: z.array(OutboundShipmentParcelV2Schema),
  customs: outboundShipmentCustomsV2Schema.nullish(),
  is_return: z.boolean().nullish(),
  showRedoLogoOnLabel: z.boolean().nullish(),
  delivery_confirmation: createEnum(OutboundShipmentDeliveryConfirmation, {
    enumName: "OutboundShipmentDeliveryConfirmation",
  }).nullish(),
  special_rates_eligibility: createEnum(SpecialRateEligibility, {
    enumName: "SpecialRateEligibility",
  }).nullish(),
  // coerce: the V2 request is persisted as JSONB in the `createOutboundShipmentRequest`
  // column, so a Date round-trips back as an ISO string and must re-parse into a Date.
  label_date: z.coerce.date().nullish(),
  tax_identifiers: z.array(OutboundShipmentTaxIdSchema).nullish(),
  incoterm: outboundShipmentIncotermSchema.nullish(),
  contents_summary: outboundShipmentContentsSummarySchema.nullish(),
  saturday_delivery: z.boolean().nullish(),
  suppressCarrierCommercialInvoice: z.boolean().nullish(),
  merchant_id: z.string().nullish(),
  payment: ShipmentPaymentOptionSchema.nullish(),
  carbon_neutral: z.boolean().nullish(),
  canada_post_option: createEnum(CanadaPostOption, {
    enumName: "CanadaPostOption",
  }).nullish(),
  direct_delivery_only: z.boolean().nullish(),
  custom_print_fields: OutboundShipmentCustomPrintFieldsSchema.nullish(),
  // Mirrors the V1 request providerInfo, with carrierAccounts added to easypost
  // (carrier_accounts is no longer a top-level field in V2).
  providerInfo: outboundShipmentRequestProviderInfoSchema
    .extend({
      // reference is nullish in V2 (required in V1's easypost) so the resolved
      // carrier accounts can be set without a reference at rate time.
      easypost: z
        .object({
          reference: z.string().nullish(),
          carrierAccounts: z.array(z.string()).nullish(),
        })
        .nullish(),
    })
    .nullish(),
  duty_payment: LandedCostBillingSchema.nullish(),
  source: createEnum(OutboundShipmentSource, {
    enumName: "OutboundShipmentSource",
  }).nullish(),
});

// A V2 rate sub-request override, scoped to a carrier + RateProvider pair. Only
// the incoterm is overridable for now: USPS and International Bridge only quote
// DAP, so an LCG shipment forced to DDP downgrades just those sub-requests back
// to DAP. Unlike the V1 request's free-form partial-request override, this is a
// single typed field — which keeps the V1 mapping trivial.
const outboundShipmentRequestV2OverridesSchema = z.partialRecord(
  createEnum(Carriers, { enumName: "Carriers" }),
  z.partialRecord(
    createEnum(RateProvider, { enumName: "RateProvider" }),
    z.object({ incoterm: outboundShipmentIncotermSchema }),
  ),
);

export const createOutboundShipmentRequestV2Schema =
  createOutboundShipmentRequestV2BaseSchema.extend({
    // Per carrier+provider rate sub-request overrides. Only the incoterm is
    // overridable (see outboundShipmentRequestV2OverridesSchema). Excluded from
    // CreateOutboundShipmentRequestV2Keys.
    overrides: outboundShipmentRequestV2OverridesSchema.nullish(),
  });

export const CreateOutboundShipmentRequestV2Schema: z.ZodType<CreateOutboundShipmentRequestV2> =
  createOutboundShipmentRequestV2Schema;

export type CreateOutboundShipmentRequestV2 = z.infer<
  typeof createOutboundShipmentRequestV2Schema
>;

export const CreateOutboundShipmentRequestV2Keys = Object.keys(
  createOutboundShipmentRequestV2BaseSchema.shape,
) as (keyof CreateOutboundShipmentRequestV2)[];

// providerInfo is resolved server-side (UPS ghost account, FedEx/DHL/UniUni
// config); is_return is forced false (outbound-only). `customs` is replaced by
// `customsRequest` — the caller declares the goods and buildOutboundRateRequest
// expands it against team settings, so the API and the fulfillment-group path
// produce the same customs form. Everything else is caller input.
const outboundRateRequestSchema = createOutboundShipmentRequestV2Schema
  .omit({ providerInfo: true, is_return: true, customs: true })
  .extend({ customsRequest: outboundCustomsRequestSchema.nullish() });

export const OutboundRateRequestSchema: z.ZodType<OutboundRateRequest> =
  outboundRateRequestSchema;
export type OutboundRateRequest = z.infer<typeof outboundRateRequestSchema>;

export enum OutboundShipmentPurchaseStatus {
  PURCHASED = "purchased",
  FAILED = "failed",
}

const outboundShipmentPackageSchema = z.object({
  labelUrl: z.string(),
  trackingCode: z.string(),
  // Carrier's public tracking page for this package, populated at buy.
  trackingUrl: z.string().nullish(),
  // This package's share of the price (IMoney) and its dimensions/weight
  // (unit-aware), populated at buy.
  rate: MoneySchema.nullish(),
  provider_cost: MoneySchema.nullish(),
  length: LengthSchema.nullish(),
  width: LengthSchema.nullish(),
  height: LengthSchema.nullish(),
  weight: WeightSchema.nullish(),
});
export type OutboundShipmentPackage = z.infer<
  typeof outboundShipmentPackageSchema
>;

/**
 * Buy-time snapshot of the purchased rate, written by savePurchasedRateInfo.
 * Field names mirror OutboundRate; `service` is a string — parse as needed.
 */
const purchasedRateInfoSchema = z.object({
  rate: MoneySchema,
  provider_cost: MoneySchema.nullish(),
  carrier: CarrierSchema,
  service: z.string(),
  carrier_account_id: z.string(),
  billing_type: z.string().nullish(),
  is_merchant_carrier_account: z.boolean().nullish(),
  is_parent_easypost_account: z.boolean().nullish(),
  payment_option: z
    .custom<ShipmentPaymentOption>()
    .meta({ annotation: "JSON" })
    .nullish(),
  providerInfo: z.custom<ProviderInfo>().meta({ annotation: "JSON" }).nullish(),
});

const OutboundShipmentSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  redoShipmentId: z.string(),
  providerBoughtShipmentId: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  purchaseStatus: createEnum(OutboundShipmentPurchaseStatus, {
    enumName: "OutboundShipmentPurchaseStatus",
  }).nullish(),
  refundStatus: createEnum(ShipmentRefundStatus, {
    enumName: "ShipmentRefundStatus",
  }).nullish(),
  // Holds either the V1 or the V2 request. Stored opaquely as a single JSONB
  // column (the `annotation: "JSON"` keeps the CongoDB schema extractor from
  // table-splitting the union). The repo accessors re-parse it with the precise
  // V1 / V2 schema, so callers never see the raw union — see
  // OutboundShipmentV1Schema / OutboundShipmentV2Schema and findOutboundShipment
  // / findV2OutboundShipment.
  createOutboundShipmentRequest: z
    .custom<CreateOutboundShipmentRequest | CreateOutboundShipmentRequestV2>()
    .meta({ annotation: "JSON" }),
  // Set on per-package child shipments (V2): points at the parent's
  // redoShipmentId. Null on V1 shipments and on V2 parents.
  parentShipmentId: z.string().nullish(),
  kind: createEnum(OutboundShipmentKind, {
    enumName: "OutboundShipmentKind",
  }).nullish(),
  shipmentPackages: z.array(outboundShipmentPackageSchema).nullish(),
  /** @deprecated Read-only for legacy docs; new buys write purchasedRateInfo.rate. */
  rate: MoneySchema.nullish(),
  /** @deprecated Read-only for legacy docs; new buys write purchasedRateInfo.provider_cost. */
  provider_cost: MoneySchema.nullish(),
  // Explicit V1/V2 discriminator. 1 (or absent, on legacy docs) ⇒ V1; 2 ⇒ V2.
  // The repo narrows on this rather than on the request's shape — see
  // OutboundShipmentV1Schema / OutboundShipmentV2Schema.
  version: z.number().nullish(),
  provider: createEnum(RateProvider, { enumName: "RateProvider" }).nullish(),
  // Buy-time snapshot of the purchased rate, so charge and void read only the doc.
  purchasedRateInfo: purchasedRateInfoSchema.nullish(),
  // Buy-time customs docs for the whole shipment (populated on international buys).
  commercialInvoiceUrl: z.string().nullish(),
  comInvoiceSubmittedElectronically: z.boolean().nullish(),
  /** @deprecated Write-only debug data; no longer persisted (we store []). */
  rates: z.array(rawOutboundRateSchema),
  // Raw provider data for debugging/auditing - stored but excluded from reads
  rawProviderRatingRequests: z.array(z.any()).nullish(),
  rawProviderRatingResponses: z.array(z.any()).nullish(),
  rawProviderPurchaseRequest: z.any().nullish(),
  rawProviderPurchaseResponse: z.any().nullish(),
  rawProviderVoidResponse: z.any().nullish(),
  rawVoidResponse: z.any().nullish(),
  cleanedAt: z.coerce.date().nullish(),
  source: createEnum(OutboundShipmentSource, {
    enumName: "OutboundShipmentSource",
  }).nullish(),
});
// The stored shape: `createOutboundShipmentRequest` is the raw V1-or-V2 union.
// Used only at the persistence boundary (the repo + CongoDB extraction); app
// code uses the narrowed OutboundShipment / OutboundShipmentV2 below.
export const OutboundShipmentSchema: z.ZodType<OutboundShipmentStored> =
  OutboundShipmentSchema_;

export interface OutboundShipmentStored extends z.infer<
  typeof OutboundShipmentSchema_
> {}

export type PurchasedRateInfo = z.infer<typeof purchasedRateInfoSchema>;

// Buy-time customs docs snapshotted onto the shipment doc, like PurchasedRateInfo.
export type PurchaseCustomsDocs = Pick<
  OutboundShipmentStored,
  "commercialInvoiceUrl" | "comInvoiceSubmittedElectronically"
>;

type OutboundShipmentCommon = Omit<
  OutboundShipmentStored,
  "createOutboundShipmentRequest"
>;

// The canonical (V1) outbound shipment — the shape every existing caller uses.
// `createOutboundShipmentRequest` is narrowed to the V1 request; the repo
// asserts this when reading (findOutboundShipment), so callers are unchanged.
export interface OutboundShipment extends OutboundShipmentCommon {
  createOutboundShipmentRequest: CreateOutboundShipmentRequest;
  version?: 1 | null;
}

// A V2 outbound shipment (parent or per-package child). Same document shape,
// but `createOutboundShipmentRequest` is the V2 request. Read via
// findV2OutboundShipment, which asserts the V2 shape.
export interface OutboundShipmentV2 extends OutboundShipmentCommon {
  createOutboundShipmentRequest: CreateOutboundShipmentRequestV2;
  version: 2;
}

// Parse schemas that narrow the stored union to a concrete request shape. These
// are NOT registered for CongoDB extraction (only OutboundShipmentSchema is);
// they exist purely so the repo can validate-and-narrow on read/write.
const OutboundShipmentV1Schema_ = OutboundShipmentSchema_.extend({
  createOutboundShipmentRequest: OutboundShipmentRequestSchema_,
  // V1 docs are version 1 or, on legacy documents, have no version at all.
  version: z.literal(1).nullish(),
});
export const OutboundShipmentV1Schema: z.ZodType<OutboundShipment> =
  OutboundShipmentV1Schema_;

const OutboundShipmentV2Schema_ = OutboundShipmentSchema_.extend({
  createOutboundShipmentRequest: createOutboundShipmentRequestV2Schema,
  version: z.literal(2),
});
export const OutboundShipmentV2Schema: z.ZodType<OutboundShipmentV2> =
  OutboundShipmentV2Schema_;

// Version-agnostic projection: omits `version`/`createOutboundShipmentRequest`,
// so it parses V1, V2, and wrap-v1-under-V2 carrier shipments uniformly.
const outboundShipmentViewSchema = OutboundShipmentSchema_.pick({
  _id: true,
  team: true,
  createdAt: true,
  updatedAt: true,
  redoShipmentId: true,
  parentShipmentId: true,
  kind: true,
  version: true,
  provider: true,
  purchaseStatus: true,
  refundStatus: true,
  providerBoughtShipmentId: true,
  trackingNumber: true,
  rate: true,
  provider_cost: true,
  purchasedRateInfo: true,
  commercialInvoiceUrl: true,
  comInvoiceSubmittedElectronically: true,
  shipmentPackages: true,
  source: true,
});
export type OutboundShipmentView = z.infer<typeof outboundShipmentViewSchema>;
export const OutboundShipmentViewSchema: z.ZodType<OutboundShipmentView> =
  outboundShipmentViewSchema;

export function joinOrderNames(
  orderNames: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  if (!orderNames) {
    return "";
  }
  const uniqueOrderNames = new Set<string>(orderNames.invoice_numbers);
  return Array.from(uniqueOrderNames).join(", ");
}

export function joinPoNumbers(
  customPrintFields: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  if (!customPrintFields?.po_numbers) {
    return "";
  }
  const uniquePoNumbers = new Set<string>(customPrintFields.po_numbers);
  return Array.from(uniquePoNumbers).join(", ");
}

export function joinOrderAndPoNumbers(
  customPrintFields: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  const orderNames = joinOrderNames(customPrintFields);
  const poNumbers = joinPoNumbers(customPrintFields);
  if (orderNames && poNumbers) {
    return `${orderNames} - ${poNumbers}`;
  }
  return orderNames || poNumbers;
}

export function buildSkuList(
  skus: OutboundShipmentCustomPrintFields["skus"],
): string[] {
  return skus.map((sku) => `${sku.sku} x${sku.quantity}`);
}

// Normalize line-item names for the label free-text field: trim, drop blanks,
// and dedupe while preserving order. Characters pass through as-is; any
// per-carrier line-length truncation is applied by the provider.
export function buildProductNameList(
  productNames: readonly string[],
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const name of productNames) {
    const trimmed = name.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result;
}

export function isPoBoxAddress({
  line1,
  line2,
}: {
  line1?: string | null;
  line2?: string | null;
}): boolean {
  const normalizedStreet1 = line1?.toLowerCase() || "";
  const normalizedStreet2 = line2?.toLowerCase() || "";
  // Not matching bare "po"/"p.o."/"p.o"/"po." for fear of false positives
  // (e.g. "Portland", "Depot St"). If more robust detection is needed, use a
  // regex anchored to a following number, e.g. /\bp\.?\s*o\.?\s*(box\s*)?#?\s*\d+/i.
  const poBoxVariants = ["po box", "p.o. box", "p.o box", "po. box", "p o box"];
  if (
    poBoxVariants.some(
      (variant) =>
        normalizedStreet1.includes(variant) ||
        normalizedStreet2.includes(variant),
    )
  ) {
    return true;
  }
  return false;
}

export function formatReference(input: string, maxLength: number): string {
  // Keep only letters, numbers, commas, dashes, and # signs. Don't go over the max length.
  return input.replace(/[^a-zA-Z0-9,#-]/g, " ").slice(0, maxLength);
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { OutboundShipmentKind } from "@redotech/cockroachdb-codegen/congodb/outbound-shipment-kind";
